import { AuthorizationError } from '@/app/common/components/AuthorizationError'
import { Input } from '@/app/common/components/input/Input'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import FormSection from '@/common/components/FormSection.tsx'
import Label from '@/common/components/Label.tsx'
import PageLoader from '@/common/components/PageLoader.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { parseRequestError } from '@/common/utilities.ts'
import FormattedPhoneInput from '@/pages/providers/components/configureStore/components/FormattedPhoneInput.tsx'
import BankingSection from '@/pages/providers/components/SchoolForm/components/banking_section/BankingSection.tsx'
import DifferentMailingAddressSection from '@/pages/providers/components/SchoolForm/components/different_mailing_address/DifferentMailingAddressSection.tsx'
import ChargeTuitionSection from '@/pages/providers/components/SchoolForm/components/do_u_charge_tuition/ChargeTuitionSection.tsx'
import CheckboxQuestion from '@/pages/providers/components/SchoolForm/components/form_elements/CheckboxQuestion.tsx'
import RadioButtonQuestion from '@/pages/providers/components/SchoolForm/components/form_elements/RadioButtonQuestion.tsx'
import StandardizedTestsSection from '@/pages/providers/components/SchoolForm/components/standardized_tests_section/StandardizedTestsSection.tsx'
import { USA_COUNTRY_CODE } from '@/pages/providers/components/SchoolForm/constants/constants.ts'
import { mapSchoolFormValues } from '@/pages/providers/components/SchoolForm/mapSchoolFormValues.ts'
import {
  ACCREDITATION_BODIES,
  ADDITIONAL_PROGRAMS,
  AREAS_OF_SPECIALIZATION,
  COED_STATUS_OPTIONS,
  HYBRID_STATUS_OPTIONS,
  LEARNING_DIFFERENCES,
  PRIMARY_CONTACT_ROLE_OPTIONS,
  RELIGIOUS_AFFILIATIONS,
  SCHOOL_GRADES,
  WHAT_AREA_IS_YOUR_SCHOOL_LOCATED_IN,
} from '@/pages/providers/components/SchoolForm/questions.constant.ts'
import { getTotalDemographicsPercentage } from '@/pages/providers/components/SchoolForm/schemas/demographics.schema.ts'
import {
  SchoolFormSchema,
  SchoolFormValues,
} from '@/pages/providers/components/SchoolForm/schoolForm.schema.ts'
import { SchoolPayload } from '@/pages/providers/components/SchoolForm/schoolForm.type.ts'
import { CommonQueries } from '@/queries/common/queries.ts'
import { StateList } from '@/queries/common/requests.ts'
import { ProviderQueries } from '@/queries/providers/queries.ts'
import { ProviderDetail } from '@/schemas/provider.schema'
import { SchoolResponseSchema } from '@/schemas/school-application.schema'
import { Checkbox } from '@/shad/ui/checkbox.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select.tsx'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { RootState } from '@/storage/store.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CurrencyInput from 'react-currency-input-field'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import AdminAuthGuard from '../../admin-auth-guard'

type Props = {
  schoolInfo: SchoolResponseSchema
  providerInfo: ProviderDetail
}

const SchoolForm = ({ schoolInfo, providerInfo }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    control,
  } = useForm<SchoolFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(SchoolFormSchema),
    defaultValues: schoolInfo
      ? mapSchoolFormValues(schoolInfo, providerInfo)
      : undefined,
  })

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const { providerId } = useParams()

  if (!providerId) {
    throw new Error('ProviderId Not found')
  }

  const programId = useSelector(selectSelectedProgram).id

  const queryClient = useQueryClient()

  const { toast } = useToast()

  const statesQuery = useQuery(
    CommonQueries.FetchStateByContry(USA_COUNTRY_CODE)
  )

  const parsedProviderId = Number(providerId)

  const mutation = useMutation(
    ProviderQueries.updateSchoolApplication(
      parsedProviderId,
      Number(schoolInfo?.id) ?? 0,
      Number(selectedProgramYear?.id) ?? 0
    )
  )

  const onSubmit = (values: SchoolFormValues) => {
    const payload: SchoolPayload = {
      school_name: values.schoolName,
      street_address: values.streetAddress,
      street_address_line_2: values.streetAddressLine2,
      city: values.city ?? undefined,
      state: values.state,
      county: values.county,
      postal_zip_code: values.postalZipCode,
      is_mailing_address_same: values.MailingAddressSchema.isMailingAddressSame,
      mailing_street_address: values.MailingAddressSchema?.mailingStreetAddress,
      mailing_street_address_line_2:
        values.MailingAddressSchema?.mailingStreetAddress2,
      mailing_city: values.MailingAddressSchema?.mailingCity,
      mailing_state: values.MailingAddressSchema?.mailingState,
      mailing_zip_code: values.MailingAddressSchema?.mailingZipCode,
      school_area_location: values.area,
      lowest_grade_served: values.lowestGrade,
      highest_grade_served: values.highestGrade,
      religious_affiliation: values.religiousAffiliation,
      coed_status: values.coedStatus,
      hybrid_status: values.hybridStatus,
      areas_of_specialization: values.areasOfSpecialization.join(', '),
      learning_differences_accommodated: values.learningDifferences.join(', '),
      additional_programs_offered: values.additionalPrograms.join(', '),
      accreditation_bodies: values.accreditationBodies.join(', '),
      charges_tuition: values.TuitionSchema?.chargeTuition,
      pre_k_tuition: values.TuitionSchema?.kindergartenTuition,
      kindergarten_tuition: values.TuitionSchema?.kindergartenTuition,
      elementary_tuition: values.TuitionSchema?.elementaryTuition,
      middle_school_tuition: values.TuitionSchema?.middleSchoolTuition,
      high_school_tuition: values.TuitionSchema?.highSchoolTuition,
      variable_tuition_forms: values.TuitionSchema?.variableTuition?.join(', '),
      per_pupil_expenditures: values.perPupilExpenditure,
      administers_standardized_test:
        values.StandardizedTestsSchema?.isDoingStandardizedTests,
      standardized_tests_administered:
        values.StandardizedTestsSchema?.howAreStudentsAssessed,
      student_assessment_methods:
        values.StandardizedTestsSchema?.standardizedTests?.join(', '),
      contracted_with_facts: values.factsContract,

      maximum_school_capacity: values.maxSchoolCapacity,
      current_enrollment: values.currentEnrollment,
      percentage_350_poverty_guidelines: values.percentLowIncome,
      additional_students_350_poverty: values.additionalLowIncome,
      student_teacher_ratio_full_time: values.ratioFullTime,
      student_teacher_ratio_all: values.ratioFullTimePartTime,

      primary_contact_first_name: values.primaryContactFirstName,
      primary_contact_last_name: values.primaryContactLastName,
      primary_contact_title: values.primaryContactTitle,
      primary_contact_phone: values.primaryContactPhone,
      primary_contact_email: values.primaryContactEmail,
      primary_contact_role: values.primaryContactRole,

      gender_female: values.DemographicsSchema?.genderFemale,
      gender_male: values.DemographicsSchema?.genderMale,
      gender_other: values.DemographicsSchema?.genderOther,

      ethnicity_african_american:
        values.DemographicsSchema?.ethnicityAfricanAmerican,
      ethnicity_asian: values.DemographicsSchema?.ethnicityAsian,
      ethnicity_caucasian: values.DemographicsSchema?.ethnicityCaucasian,
      ethnicity_east_asian: values.DemographicsSchema?.ethnicityEastAsian,
      ethnicity_hispanic_latino:
        values.DemographicsSchema?.ethnicityHispanicLatino,
      ethnicity_middle_eastern:
        values.DemographicsSchema?.ethnicityMiddleEastern,
      ethnicity_multiracial: values.DemographicsSchema?.ethnicityMultiracial,
      ethnicity_native_american:
        values.DemographicsSchema?.ethnicityNativeAmerican,
      ethnicity_native_hawaiian:
        values.DemographicsSchema?.ethnicityNativeHawaiian,
      ethnicity_prefer_not_to_say:
        values.DemographicsSchema?.ethnicityPreferNot,

      percentage_esl_students: values.percentStudentsESL,
      percentage_esl_parents: values.percentParentsESL,

      new_school_to_ace: values.isNewSchool,
      year_established: values.yearEstablished,
      history_of_school: values.schoolHistory,
      school_website: values.schoolWebsite,
      online_information_source: values.onlinePresence,
      mission_statement: values.missionStatement,

      receives_ach_payment: values.BankingSchema?.canReceiveACH,
      federal_tax_id: values.BankingSchema?.federalTaxId,
      account_type: values.BankingSchema?.accountType,
      depository_name: values.BankingSchema?.depositoryName,
      bank_branch: values.BankingSchema?.branch,
      bank_street_address: values.BankingSchema?.bankStreetAddress,
      bank_street_address_line_2: values.BankingSchema?.bankStreetAddress2,
      bank_city: values.BankingSchema?.bankCity,
      bank_state: values.BankingSchema?.bankState,
      bank_routing_id: values.BankingSchema?.bankRoutingId,
      account_number: values.BankingSchema?.accountNumber,
      receive_physical_checks: values.BankingSchema?.canReceiveACH,
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    mutation.mutate(payload, {
      onSuccess: () => {
        toast({
          title: 'School information Updated',
          description: 'School information has been updated successfully.',
          variant: 'default',
        })
        queryClient.invalidateQueries(
          ProviderQueries.getProviderById(programId, providerId)
        )
      },
      onError: (error) => {
        const errorMessage = parseRequestError(error).message
        toast({
          title: 'Update Failed',
          description: errorMessage,
          variant: 'destructive',
        })
      },
    })
  }

  function onError(error: unknown) {
    console.log(error)
  }

  return (
    <AdminAuthGuard
      fallbackComponent={<AuthorizationError />}
      requiredPermissions={[
        'provider_management_create',
        'provider_management_edit',
      ]}
    >
      <PageLoader query={statesQuery}>
        {(states: StateList) => {
          return (
            <Card>
              <CardHeader className="flex justify-between items-center text-md font-semibold">
                <h1
                  id="form-title"
                  className="text-left text-md font-semibold text-gray-900"
                >
                  School Information
                </h1>
              </CardHeader>
              <CardContent className="p-0 lg:p-0 card-body grid gap-5">
                <div className="px-4 py-6 sm:p-8">
                  <form onSubmit={handleSubmit(onSubmit, onError)}>
                    <h2 className="text-gray-800 font-semibold text-xl">
                      School Partnership Application and Agreement
                    </h2>
                    <p className="text-gray-600 text-sm">
                      Thank you for taking the time to complete this form which
                      will provide ACE Scholarships with important information
                      about your school. Please note, the first time completing
                      this form you will need to enter all information in its
                      entirety and not be able to 'save and return'. Once
                      submitted, and your school has been approved as a Partner
                      School, you will be able to revisit and edit the
                      information at any time.
                    </p>
                    <div className="pb-10 ">
                      <h3 className="font-semibold text-md text-gray-800 pt-8 pb-4">
                        Partner School Information
                      </h3>
                    </div>
                    <FormSection>
                      <Label htmlFor="schoolName">
                        School Name<span className="text-red-500">*</span>
                      </Label>
                      <Input id="schoolName" {...register('schoolName')} />
                      <FormErrorMessage message={errors.schoolName?.message} />
                    </FormSection>

                    <FormSection>
                      <Label htmlFor="streetAddress">
                        Street Address<span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="streetAddress"
                        {...register('streetAddress')}
                      />
                      <FormErrorMessage
                        message={errors.streetAddress?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="streetAddress2">
                        Street Address Line 2
                      </Label>
                      <Input
                        id="streetAddress2"
                        {...register('streetAddressLine2')}
                      />
                      <FormErrorMessage
                        message={errors.streetAddressLine2?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="city">
                        City<span className="text-red-500">*</span>
                      </Label>
                      <Input id="city" {...register('city')} />
                      <FormErrorMessage message={errors.city?.message} />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="state">
                        State<span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={watch('state')}
                        onValueChange={(value) =>
                          setValue('state', value, { shouldValidate: true })
                        }
                        name="state"
                      >
                        <SelectTrigger id="state" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {states.data.map((state) => (
                            <SelectItem
                              key={state.id}
                              value={state.id.toString()}
                            >
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormErrorMessage message={errors.state?.message} />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="county">
                        County<span className="text-red-500">*</span>
                      </Label>
                      <Input id="county" {...register('county')} />
                      <FormErrorMessage message={errors.county?.message} />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="postalCode">
                        Postal/Zip Code<span className="text-red-500">*</span>
                      </Label>
                      <Input id="postalCode" {...register('postalZipCode')} />
                      <FormErrorMessage
                        message={errors.postalZipCode?.message}
                      />
                    </FormSection>
                    <DifferentMailingAddressSection
                      control={control}
                      errors={errors}
                      states={states}
                      watch={watch}
                    />
                    <div className="pb-10">
                      <FormSection>
                        {/* Area */}
                        <Controller
                          name="area"
                          control={control}
                          render={({ field }) => (
                            <RadioButtonQuestion
                              question={'In what area is your school located?'}
                              options={WHAT_AREA_IS_YOUR_SCHOOL_LOCATED_IN}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        <FormErrorMessage message={errors.area?.message} />
                      </FormSection>
                    </div>
                    <div className="pb-10">
                      <FormSection>
                        {/* Lowest Grade */}
                        <Label>
                          Lowest Grade Served
                          <span className="text-red-500">*</span>
                        </Label>
                        <p className="text-xs text-gray-600 mb-2">
                          *ACE does not award scholarships to students in PK in
                          AR, CO, KS, LA, MO, MS, NM, UT, TX, & WY.
                        </p>
                        <Controller
                          name="lowestGrade"
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={(value) => field.onChange(value)}
                              name="lowestGrade"
                            >
                              <SelectTrigger
                                id="lowestGrade"
                                className="w-full"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SCHOOL_GRADES.map((grade) => (
                                  <SelectItem key={grade} value={grade}>
                                    {grade}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FormErrorMessage
                          message={errors.lowestGrade?.message}
                        />
                      </FormSection>
                      <FormSection>
                        {/* Highest Grade */}
                        <Label>
                          Highest Grade Served
                          <span className="text-red-500">*</span>
                        </Label>

                        <Controller
                          name="highestGrade"
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={(value) => field.onChange(value)}
                              name="highestGrade"
                            >
                              <SelectTrigger
                                id="highestGrade"
                                className="w-full"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SCHOOL_GRADES.map((grade) => (
                                  <SelectItem key={grade} value={grade}>
                                    {grade}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FormErrorMessage
                          message={errors.highestGrade?.message}
                        />
                      </FormSection>
                      <FormSection>
                        {/* Religious Affiliation */}
                        <Label>
                          Religious Affiliation
                          <span className="text-red-500">*</span>
                        </Label>
                        <Controller
                          name="religiousAffiliation"
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              name="religiousAffiliation"
                            >
                              <SelectTrigger
                                id="religiousAffiliation"
                                className="w-full"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {RELIGIOUS_AFFILIATIONS.map((affiliation) => (
                                  <SelectItem
                                    key={affiliation}
                                    value={affiliation}
                                  >
                                    {affiliation}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FormErrorMessage
                          message={errors.religiousAffiliation?.message}
                        />
                      </FormSection>
                    </div>
                    <FormSection>
                      {/* Co-ed Status */}
                      <Controller
                        name="coedStatus"
                        control={control}
                        render={({ field }) => (
                          <RadioButtonQuestion
                            question={'Co-ed Status'}
                            options={COED_STATUS_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage message={errors.coedStatus?.message} />
                    </FormSection>
                    <FormSection>
                      {/* Hybrid Status */}
                      <Controller
                        name="hybridStatus"
                        control={control}
                        render={({ field }) => (
                          <RadioButtonQuestion
                            question={'Hybrid Status'}
                            options={HYBRID_STATUS_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.hybridStatus?.message}
                      />
                    </FormSection>
                    <FormSection>
                      {/* Areas of Specialization */}
                      <Controller
                        name="areasOfSpecialization"
                        control={control}
                        render={({ field }) => (
                          <CheckboxQuestion
                            question={'Area(s) of Specialization'}
                            options={AREAS_OF_SPECIALIZATION}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.areasOfSpecialization?.message}
                      />
                    </FormSection>
                    <FormSection>
                      {/* Learning Differences/Exceptionalities */}
                      <Controller
                        name="learningDifferences"
                        control={control}
                        render={({ field }) => (
                          <CheckboxQuestion
                            question={
                              'Learning Differences/Exceptionalities Accommodated'
                            }
                            options={LEARNING_DIFFERENCES}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.learningDifferences?.message}
                      />
                    </FormSection>
                    <FormSection>
                      {/* Additional Programs */}
                      <Controller
                        name="additionalPrograms"
                        control={control}
                        render={({ field }) => (
                          <CheckboxQuestion
                            question={'Additional Program(s) Offered'}
                            options={ADDITIONAL_PROGRAMS}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.additionalPrograms?.message}
                      />
                    </FormSection>
                    <FormSection>
                      {/* Accreditation Bodies */}
                      <Controller
                        name="accreditationBodies"
                        control={control}
                        render={({ field }) => (
                          <CheckboxQuestion
                            question={
                              'Is your school accredited by any of the following bodies? (select all that apply)'
                            }
                            options={ACCREDITATION_BODIES}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.accreditationBodies?.message}
                      />
                    </FormSection>
                    <ChargeTuitionSection
                      control={control}
                      errors={errors}
                      watch={watch}
                    />
                    <FormSection>
                      <Label htmlFor="perPupilExpenditure">
                        Per Pupil Expenditures (Cost to Educate)
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="text-xs text-gray-600 mb-2">
                        Average amount spent per pupil across all students. This
                        includes expenditures for the classrooms, salaries,
                        administration, buildings, and any classroom materials.
                        Does not include development costs. (May be lower or
                        higher than published tuition rate.)
                      </div>
                      <Controller
                        control={control}
                        name="perPupilExpenditure"
                        render={({ field }) => {
                          return (
                            <CurrencyInput
                              className={inputVariants({
                                variant: 'text',
                              })}
                              prefix="$"
                              value={field.value}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )
                        }}
                      />
                      <FormErrorMessage
                        message={errors.perPupilExpenditure?.message}
                      />
                    </FormSection>
                    <StandardizedTestsSection
                      control={control}
                      watch={watch}
                      errors={errors}
                    />
                    <FormSection>
                      <Controller
                        name="factsContract"
                        control={control}
                        render={({ field }) => (
                          <RadioButtonQuestion
                            question={
                              'Is your school contracted with FACTS for grant and aid management?'
                            }
                            options={['Yes', 'No']}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormErrorMessage
                        message={errors.factsContract?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="maxSchoolCapacity">
                        Maximum School Capacity
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="maxSchoolCapacity"
                        type="number"
                        min="0"
                        {...register('maxSchoolCapacity')}
                      />
                      <FormErrorMessage
                        message={errors.maxSchoolCapacity?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="currentEnrollment">
                        Current Enrollment
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="currentEnrollment"
                        type="number"
                        min="0"
                        {...register('currentEnrollment')}
                      />
                      <FormErrorMessage
                        message={errors.currentEnrollment?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="percentLowIncome">
                        What percentage of your students are 350% or less of
                        Federal Poverty Guidelines?
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="percentLowIncome"
                        type="text"
                        {...register('percentLowIncome')}
                      />
                      <FormErrorMessage
                        message={errors.percentLowIncome?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="additionalLowIncome">
                        How many additional students with family income 350% or
                        less of Federal Poverty Guidelines would you like to
                        accommodate in the coming school year?
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="additionalLowIncome"
                        type="number"
                        min="0"
                        {...register('additionalLowIncome')}
                      />
                      <FormErrorMessage
                        message={errors.additionalLowIncome?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="ratioFullTime">
                        How many students do you have per teacher (student to
                        teacher ratio)? (Full Time Only)
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="ratioFullTime"
                        type="number"
                        min="0"
                        {...register('ratioFullTime')}
                      />
                      <FormErrorMessage
                        message={errors.ratioFullTime?.message}
                      />
                    </FormSection>
                    <FormSection>
                      <Label htmlFor="ratioFullTimePartTime">
                        How many students do you have per teacher (student to
                        teacher ratio)? (Full Time and Part Time)
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="ratioFullTimePartTime"
                        type="number"
                        min="0"
                        {...register('ratioFullTimePartTime')}
                      />
                      <FormErrorMessage
                        message={errors.ratioFullTimePartTime?.message}
                      />
                    </FormSection>
                    <div className="py-10">
                      <h3 className="text-2xl font-medium  mb-4">
                        School Contact Information
                      </h3>
                      <FormSection>
                        <Label htmlFor="primaryContactFirstName">
                          Primary Contact First Name
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="primaryContactFirstName"
                          {...register('primaryContactFirstName')}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactFirstName?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="primaryContactLastName">
                          Primary Contact Last Name
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="primaryContactLastName"
                          {...register('primaryContactLastName')}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactLastName?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="primaryContactTitle">
                          Primary Contact Title
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="primaryContactTitle"
                          {...register('primaryContactTitle')}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactTitle?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Controller
                          control={control}
                          name="primaryContactPhone"
                          render={({ field }) => {
                            return (
                              <FormattedPhoneInput
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                value={field.value}
                                label={
                                  <>
                                    Primary Contact Phone Number{' '}
                                    <span className="text-red-500">*</span>
                                  </>
                                }
                              />
                            )
                          }}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactPhone?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="primaryContactEmail">
                          Primary Contact Email Address
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="primaryContactEmail"
                          type="email"
                          {...register('primaryContactEmail')}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactEmail?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Controller
                          name="primaryContactRole"
                          control={control}
                          render={({ field }) => (
                            <RadioButtonQuestion
                              question={'Primary Contact Role'}
                              options={PRIMARY_CONTACT_ROLE_OPTIONS}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        <FormErrorMessage
                          message={errors.primaryContactRole?.message}
                        />
                      </FormSection>
                    </div>

                    <div className="py-10 space-y-4">
                      <FormSection>
                        <h3 className="text-2xl text-gray-800 font-medium mb-4">
                          Gender Breakdown of Student Population
                        </h3>
                        <Label htmlFor="genderFemale">
                          Female<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="genderFemale"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.genderFemale')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.genderFemale?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="genderMale">
                          Male<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="genderMale"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.genderMale')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.genderMale?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="genderOther">
                          Other/Prefer Not to Say
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="genderOther"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.genderOther')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.genderOther?.message
                          }
                        />
                      </FormSection>
                      <div className="font-semibold mt-2">
                        Total:{' '}
                        {Number(watch('DemographicsSchema.genderFemale') || 0) +
                          Number(watch('DemographicsSchema.genderMale') || 0) +
                          Number(watch('DemographicsSchema.genderOther') || 0)}
                        %
                      </div>
                    </div>
                    <div className="py-10">
                      <FormSection>
                        <h3 className="text-2xl mb-4 font-medium">
                          Ethnicity Breakdown of Student Population
                        </h3>
                        <Label htmlFor="ethnicityAfricanAmerican">
                          African American/Black
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityAfricanAmerican"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityAfricanAmerican'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityAfricanAmerican
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityAsian">
                          Asian<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityAsian"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.ethnicityAsian')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityAsian?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityCaucasian">
                          Caucasian/White<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityCaucasian"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.ethnicityCaucasian')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityCaucasian
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityEastAsian">
                          East Asian<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityEastAsian"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.ethnicityEastAsian')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityEastAsian
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityHispanicLatino">
                          Hispanic Latino<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityHispanicLatino"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityHispanicLatino'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityHispanicLatino
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityMiddleEastern">
                          Middle Eastern/North African
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityMiddleEastern"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityMiddleEastern'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityMiddleEastern
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityMultiracial">
                          Multiracial<span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityMultiracial"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityMultiracial'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityMultiracial
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityNativeAmerican">
                          Native American/Alaskan
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityNativeAmerican"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityNativeAmerican'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityNativeAmerican
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityNativeHawaiian">
                          Native Hawaiian/Pacific Islander
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityNativeHawaiian"
                          type="number"
                          min="0"
                          max="100"
                          {...register(
                            'DemographicsSchema.ethnicityNativeHawaiian'
                          )}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityNativeHawaiian
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="ethnicityPreferNot">
                          Prefer Not to Say/Unknown
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="ethnicityPreferNot"
                          type="number"
                          min="0"
                          max="100"
                          {...register('DemographicsSchema.ethnicityPreferNot')}
                        />
                        <FormErrorMessage
                          message={
                            errors.DemographicsSchema?.ethnicityPreferNot
                              ?.message
                          }
                        />
                      </FormSection>
                      <FormSection>
                        {watch('DemographicsSchema') && (
                          <div className="font-semibold mt-2">
                            Total:{' '}
                            {getTotalDemographicsPercentage(
                              getValues('DemographicsSchema')
                            )}
                            %
                          </div>
                        )}
                      </FormSection>
                    </div>
                    <div className="py-10">
                      <FormSection>
                        <h3 className="text-3xl font-medium mb-6">
                          Language Preference
                        </h3>
                        <Label htmlFor="percentStudentsESL">
                          Percentage of students attending the school in the
                          most recent school year for whom English is a second
                          language
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="percentStudentsESL"
                          type="number"
                          min="0"
                          max="100"
                          {...register('percentStudentsESL')}
                        />
                        <FormErrorMessage
                          message={errors.percentStudentsESL?.message}
                        />
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="percentParentsESL">
                          Percentage of parents of students in the most recent
                          school year for whom English is a second language
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="percentParentsESL"
                          type="number"
                          min="0"
                          max="100"
                          {...register('percentParentsESL')}
                        />
                        <FormErrorMessage
                          message={errors.percentParentsESL?.message}
                        />
                      </FormSection>
                    </div>
                    <div>
                      <h3 className="font-medium text-2xl mb-6">
                        About Your School
                      </h3>
                      <FormSection>
                        {/* Are you a new school to ACE? */}
                        <Controller
                          name="isNewSchool"
                          control={control}
                          render={({ field }) => (
                            <RadioButtonQuestion
                              question="Are you a new school to ACE for the 25-26 School year?"
                              options={['Yes', 'No']}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        <FormErrorMessage
                          message={errors.isNewSchool?.message}
                        />
                      </FormSection>
                      <FormSection>
                        {/* Year Established */}
                        <Label htmlFor="yearEstablished">
                          What year was your school established?
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="yearEstablished"
                          type="number"
                          min="1000"
                          max={new Date().getFullYear()}
                          {...register('yearEstablished')}
                        />
                        <FormErrorMessage
                          message={errors.yearEstablished?.message}
                        />
                      </FormSection>
                      <FormSection>
                        {/* History of School */}
                        <Label htmlFor="schoolHistory">
                          History of School
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="schoolHistory"
                          {...register('schoolHistory')}
                        />
                        <FormErrorMessage
                          message={errors.schoolHistory?.message}
                        />
                        {/* School Website */}
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="schoolWebsite">School Website</Label>
                        <Input
                          id="schoolWebsite"
                          type="text"
                          {...register('schoolWebsite')}
                        />
                        <FormErrorMessage
                          message={errors.schoolWebsite?.message}
                        />
                        {/* Online Presence */}
                      </FormSection>
                      <FormSection>
                        <Label htmlFor="onlinePresence">
                          If you do not have a website, where else are we able
                          to find information about your school online? ex:
                          social media.
                        </Label>
                        <Input
                          id="onlinePresence"
                          {...register('onlinePresence')}
                        />
                        <FormErrorMessage
                          message={errors.onlinePresence?.message}
                        />
                      </FormSection>
                      <FormSection>
                        {/* Mission Statement */}
                        <Label htmlFor="missionStatement">
                          Mission Statement
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="missionStatement"
                          {...register('missionStatement')}
                        />
                        <FormErrorMessage
                          message={errors.missionStatement?.message}
                        />
                      </FormSection>
                      {/* School Logo Upload */}

                      {/*{!initialValues && (*/}
                      {/*  <FormSection>*/}
                      {/*    <Label htmlFor="schoolLogo">School Logo (Optional)</Label>*/}
                      {/*    <ImageUploadToBase64*/}
                      {/*      onBase64Change={(base64) => {*/}
                      {/*        setValue("schoolLogo", base64);*/}
                      {/*      }}*/}
                      {/*    />*/}
                      {/*    <FormErrorMessage message={errors.schoolLogo?.message} />*/}
                      {/*  </FormSection>*/}
                      {/*)}*/}

                      {/*{initialValues && Object.keys(initialValues).length > 0 && (*/}
                      {/*  <FormSection>*/}
                      {/*    <Label htmlFor="schoolLogo">School Logo (Optional)</Label>*/}

                      {/*    {!previewLogo && (!initialValues.schoolLogo || logoRemoved) && (*/}
                      {/*      <>*/}
                      {/*        <BasicDocumentUpload*/}
                      {/*          onUpload={handleLogoUpload}*/}
                      {/*          allowMultiple={false}*/}
                      {/*          isPdfAllowed={false}*/}
                      {/*        />*/}
                      {/*        {errors.schoolLogo && (*/}
                      {/*          <FormErrorMessage message={errors.schoolLogo.message} />*/}
                      {/*        )}*/}
                      {/*      </>*/}
                      {/*    )}*/}

                      {/*    {!previewLogo && initialValues.schoolLogo && !logoRemoved && (*/}
                      {/*      <div className="mt-4 flex flex-col items-center">*/}
                      {/*        <div className="border border-gray-300 rounded p-2">*/}
                      {/*          <Image*/}
                      {/*            path={initialValues.schoolLogo}*/}
                      {/*            alt="Uploaded School Logo"*/}
                      {/*            className="max-w-xs max-h-64 mx-auto object-contain"*/}
                      {/*          />*/}
                      {/*        </div>*/}
                      {/*        <Button*/}
                      {/*          variant="outline"*/}
                      {/*          className="mt-4"*/}
                      {/*          onClick={() => {*/}
                      {/*            setPreviewLogo(null);*/}
                      {/*            setLogoRemoved(true);*/}
                      {/*            setValue("schoolLogo", null, { shouldValidate: true });*/}
                      {/*          }}*/}
                      {/*        >*/}
                      {/*          Remove Image*/}
                      {/*        </Button>*/}
                      {/*      </div>*/}
                      {/*    )}*/}

                      {/*    {previewLogo && (*/}
                      {/*      <div className="mt-4">*/}
                      {/*        <div className="border border-gray-300 rounded p-2">*/}
                      {/*          <img*/}
                      {/*            src={previewLogo}*/}
                      {/*            alt="Preview"*/}
                      {/*            className="max-w-xs max-h-64 mx-auto object-contain"*/}
                      {/*          />*/}
                      {/*        </div>*/}
                      {/*        <Button*/}
                      {/*          variant="outline"*/}
                      {/*          className="mt-4"*/}
                      {/*          onClick={() => {*/}
                      {/*            setPreviewLogo(null);*/}
                      {/*            setLogoRemoved(true);*/}
                      {/*            setValue("schoolLogo", null, { shouldValidate: true });*/}
                      {/*          }}*/}
                      {/*        >*/}
                      {/*          Remove Image*/}
                      {/*        </Button>*/}
                      {/*      </div>*/}
                      {/*    )}*/}
                      {/*  </FormSection>*/}
                      {/*)}*/}

                      <BankingSection
                        states={states}
                        watch={watch}
                        errors={errors}
                        control={control}
                      />
                      {/* Attestation */}
                      <FormSection className="mt-10">
                        <div className="flex items-start gap-2">
                          <Controller
                            name="attestation"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                onCheckedChange={field.onChange}
                                onBlur={field.onBlur}
                                checked={field.value}
                              />
                            )}
                          />
                          <Label htmlFor="attestation">
                            I hereby attest that all information contained
                            herein is true and correct
                          </Label>
                        </div>
                        <FormErrorMessage
                          message={
                            errors.attestation?.message as string | undefined
                          }
                        />
                      </FormSection>
                      {/*     <Button
                type="button"
                onClick={autoFillForm}
                className="w-full mb-4"
                variant="outline"
              >
                Auto Fill Form
              </Button> */}
                      {/*<SubmitButton*/}
                      {/*  buttonTxt={initialValues ? "Save Changes" : "Submit Application"}*/}
                      {/*  status={createSchoolApplication.status}*/}
                      {/*  className="w-full mt-4"*/}
                      {/*/>*/}
                    </div>
                    <div className="flex justify-end">
                      <div>
                        <SubmitButton
                          buttonTxt="Save changes"
                          status={mutation.status}
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </CardContent>
            </Card>
          )
        }}
      </PageLoader>
    </AdminAuthGuard>
  )
}
export default SchoolForm
